import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../common_modal/Modal";
import FLOAT_MENU from "../common_trivial/FLOAT_MENU";
import { upload_image_table } from "../../App/actions/layerActions";
import dict from "../../Data/dict.json";
import generate_short_uuid from "../../App/validation/generate_short_uuid";
import icon_upload from "../../Assets/svg/icon_upload.svg";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_TYPES = [
  "csv",
  "txt",
  "rtf",
  "md",
  "markdown",
  "doc",
  "docx",
  "msword",
  "vnd.openxmlformats-officedocument.wordprocessingml.document",
  "xls",
  "xlsx",
  "vnd.ms-excel",
  "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "ppt",
  "pptx",
  "vnd.ms-powerpoint",
  "vnd.openxmlformats-officedocument.presentationml.presentation",
  "odt",
  "ods",
  "odp",
  "vnd.oasis.opendocument.text",
  "vnd.oasis.opendocument.spreadsheet",
  "vnd.oasis.opendocument.presentation",
  "pdf",
  "zip",
  "x-zip-compressed",
  "rar",
  "vnd.rar",
  "x-rar",
  "octet-stream",
  "wps-writer",
  "svg",
  "svg+xml",
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
];

const checkIsViewable = (url) => {
  if (!url) return false;
  try {
    const urlWithoutQuery = url.split("?")[0];
    const ext = urlWithoutQuery.split(".").pop().toLowerCase();

    // Natively viewable by standard web browsers
    const nativeTypes = [
      "pdf",
      "png",
      "jpg",
      "jpeg",
      "gif",
      "webp",
      "svg",
      "txt",
      "csv",
      "md",
      "markdown",
    ];
    // Viewable via Microsoft Office Viewer
    const officeTypes = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];

    return nativeTypes.includes(ext) || officeTypes.includes(ext);
  } catch (e) {
    return false;
  }
};

export default function C_E_DOC({
  editorState,
  closeEditor,
  onSave,

  row: popupRow,
  field: popupField,
  inPopup = false,

  // Common
  language = "id",
}) {
  const [modalError, setModalError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false); // New state for viewing iframe
  const dispatch = useDispatch();
  const fileInput = useRef(null);

  const layer_state = useSelector((state) => state.layer);
  const properties_state = useSelector((state) => state.properties);
  const feature_key_nested_table = properties_state?.feature_key_nested_table;
  const layer_id = layer_state?.layer_id;

  // 1. Determine active data based on mode
  const activeRow = inPopup ? popupRow : editorState?.row;
  const activeField = inPopup ? popupField : editorState?.field;
  const currentValue = activeRow?.properties?.[activeField?.key];

  // Helper to extract clean file name from standard strings or full URLs
  const displayValue =
    typeof currentValue === "string" && currentValue
      ? decodeURIComponent(currentValue.split("/").pop().split("?")[0])
      : null;

  // Formats the src for the iframe. Uses MS viewer for office docs.
  const getIframeSrc = (url) => {
    if (!url) return "";
    try {
      const urlWithoutQuery = url.split("?")[0];
      const ext = urlWithoutQuery.split(".").pop().toLowerCase();
      const officeTypes = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];

      if (officeTypes.includes(ext)) {
        // Appends the document URL to the Microsoft Web Viewer
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
      return url; // Native rendering for PDF, Images, TXT, etc.
    } catch (e) {
      return url;
    }
  };

  const processFile = (file) => {
    if (!file) return;

    // 1. Check File Size
    if (file.size > MAX_FILE_SIZE) {
      setModalError(
        dict?.["File size is too big. Maximum 100MB."]?.[language] ||
          "File size is too big. Maximum 100MB allowed.",
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }

    // 2. Check File Type
    const fileExt = file.name.split(".").pop().toLowerCase();
    const fileMimeSubtype = file.type.split("/").pop().toLowerCase();

    if (
      !ALLOWED_TYPES.includes(fileExt) &&
      !ALLOWED_TYPES.includes(fileMimeSubtype)
    ) {
      setModalError(
        dict?.["Invalid file type"]?.[language] ||
          "Invalid file type. Please upload a supported document format.",
      );
      if (fileInput.current) fileInput.current.value = "";
      return;
    }

    const body_file = {
      file: file,
      file_name: `${file?.name}_%${generate_short_uuid()}`,
      dirname: "properties",
    };

    let body_database;
    let is_nested = !!activeRow?.child_uuid;

    if (is_nested) {
      body_database = {
        geo_layer_id: layer_id,
        feature_key: feature_key_nested_table,
        child_uuid: activeRow.child_uuid,
        properties_key: activeField?.key,
        properties_value: "",
      };
    } else {
      body_database = {
        geo_layer_id: layer_id,
        feature_key: activeRow?.key,
        properties_key: activeField?.key,
        properties_value: "",
      };
    }

    const body = {
      body_file,
      body_database,
      mode: "document",
      is_nested,
    };

    dispatch(upload_image_table(body));

    // Only close editor if in table mode
    if (!inPopup && closeEditor) {
      closeEditor();
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSave && activeRow && activeField) {
      // Trigger table's handleSaveData with an empty string
      onSave(activeRow.id, activeField.key, "", currentValue);
    }
    if (!inPopup && closeEditor) {
      closeEditor();
    }
  };

  // --- Drag and Drop Handlers ---
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleInputChange = (e) => {
    processFile(e.target.files[0]);
  };

  const handleClose = () => {
    if (!inPopup && closeEditor) {
      closeEditor();
    }
  };

  const renderContent = () => (
    <div
      style={{
        padding: inPopup ? "5px 0" : "15px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: inPopup ? "100%" : "250px",
      }}
    >
      {!inPopup && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <strong style={{ fontSize: "14px", color: "#333" }}>
            {dict?.["Upload Document"]?.[language] || "Upload Document"}
          </strong>
          <button
            className="badge_small cursor_pointer background_grey_light"
            style={{ border: "none", borderRadius: "4px", outline: "none" }}
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      )}

      {displayValue && (
        <div style={{ marginBottom: "2px" }}>
          <span
            style={{
              fontSize: "12px",
              color: "#666",
              display: "block",
              marginBottom: "6px",
            }}
          >
            {dict?.["Current file"]?.[language] || "Current file"}:
          </span>
          <div
            style={{
              padding: "8px 12px",
              backgroundColor: "#f4f6f8",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              wordBreak: "break-all",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#64748b"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
              <polyline points="13 2 13 9 20 9" />
            </svg>
            <span
              style={{
                fontSize: "12px",
                color: "#475569",
                lineHeight: "1.4",
                flex: 1,
              }}
            >
              {displayValue}
            </span>

            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
              {checkIsViewable(currentValue) && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setViewModalOpen(true);
                  }}
                  style={{
                    padding: "4px 8px",
                    fontSize: "10px",
                    fontWeight: "bold",
                    color: "#fff",
                    backgroundColor: "#333",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {dict?.["View"]?.[language] || "View"}
                </button>
              )}
              {currentValue && (
                <a
                  href={currentValue}
                  target="_blank"
                  rel="noreferrer"
                  download
                  style={{ textDecoration: "none", display: "inline-block" }}
                >
                  <button
                    style={{
                      padding: "4px 8px",
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: "#333",
                      backgroundColor: "#e2e8f0",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {dict?.["Download"]?.[language] || "Download"}
                  </button>
                </a>
              )}
              <button
                onClick={handleDelete}
                style={{
                  padding: "4px 8px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  color: "#fff",
                  backgroundColor: "#dc2626", // Red for delete button
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {dict?.["Delete"]?.[language] || "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <label
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          cursor: "pointer",
          padding: "24px 16px",
          border: `2px dashed ${isDragging ? "#6666ff" : "#9ca8bb"}`,
          borderRadius: "8px",
          backgroundColor: isDragging ? "rgba(102, 102, 255, 0.05)" : "#fcfdfe",
          transition: "all 0.2s ease",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
            backgroundColor: isDragging ? "#6666ff" : "#e6eef3",
            border: `1px solid ${isDragging ? "#6666ff" : "#9ca8bb"}`,
            borderRadius: "50%",
            boxSizing: "border-box",
            transition: "background-color 0.2s ease",
          }}
        >
          <img
            src={icon_upload}
            style={{
              width: "16px",
              height: "16px",
              filter: isDragging ? "brightness(0) invert(1)" : "none",
            }}
            alt="upload system"
          />
        </div>

        <span
          style={{
            fontSize: "12px",
            color: isDragging ? "#6666ff" : "#555",
            fontWeight: "500",
            lineHeight: "1.4",
          }}
        >
          {dict?.["Click or drag file here"]?.[language] ||
            "Click or drag file here"}
        </span>

        <input
          id={`${activeRow?.key}-${activeField?.key}`}
          style={{ display: "none" }}
          type="file"
          ref={fileInput}
          onChange={handleInputChange}
          accept=".csv,.txt,.rtf,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.pdf,.zip,.rar,.svg,.png,.jpg,.jpeg,.gif,.webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar,application/rtf,text/markdown,text/plain,text/csv,image/*"
        />
      </label>
    </div>
  );

  const renderErrorModal = () =>
    modalError && (
      <Modal
        modalSize="small"
        id="modal-error-doc"
        isOpen={!!modalError}
        onClose={() => setModalError("")}
      >
        <div
          className="box-body"
          id="box-body"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "30px",
            gap: "20px",
          }}
        >
          <span
            style={{
              color: "#d9534f",
              fontSize: "16px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {modalError}
          </span>
          <button
            onClick={() => setModalError("")}
            className="button background_black"
            style={{
              padding: "8px 16px",
              backgroundColor: "#333333",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {dict?.["Close"]?.[language] || "Close"}
          </button>
        </div>
      </Modal>
    );

  const renderViewModal = () =>
    viewModalOpen && (
      <Modal
        modalSize="full"
        id="modal-view-iframe"
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title="View Document"
      >
        <div
          className="box-body"
          id="box-body"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "80vh",
            padding: "15px",
            gap: "10px",
          }}
        >
          <div
            style={{
              flex: 1,
              backgroundColor: "#f4f6f8",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <iframe
              src={getIframeSrc(currentValue)}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Document Viewer"
            />
          </div>
        </div>
      </Modal>
    );

  if (inPopup) {
    const mode_map = properties_state.mode_map;
    return (
      <main>
        {mode_map === "editor" ? (
          <section>
            {renderContent()}
            {renderErrorModal()}
            {renderViewModal()}
          </section>
        ) : (
          <div className="container background_white text_medium">
            {displayValue || "-"}
            {currentValue && (
              <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                {checkIsViewable(currentValue) && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setViewModalOpen(true);
                    }}
                    className="button_small background_black"
                  >
                    {dict?.["View"]?.[language] || "View"}
                  </button>
                )}
                <a
                  href={currentValue}
                  target="_blank"
                  rel="noreferrer"
                  download
                  style={{ textDecoration: "none", display: "block" }}
                >
                  <button className="button_small background_black">
                    {dict?.["Download"]?.[language] || "Download"}
                  </button>
                </a>
                <button
                  onClick={handleDelete}
                  className="button_small"
                  style={{
                    backgroundColor: "#dc2626",
                    color: "white",
                    border: "none",
                  }}
                >
                  {dict?.["Delete"]?.[language] || "Delete"}
                </button>
              </div>
            )}
            {renderViewModal()}
          </div>
        )}
      </main>
    );
  }

  return (
    <>
      <FLOAT_MENU
        id="global-cell-editor-doc"
        open={true}
        anchorEl={editorState?.anchorEl}
        onClose={handleClose}
        zIndex={3} // FIX: Lower zIndex specific to this instance so Modals (zIndex 4) sit on top!
      >
        {renderContent()}
      </FLOAT_MENU>
      {renderErrorModal()}
      {renderViewModal()}
    </>
  );
}
